import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS, apiRequest } from '@/lib/api';

interface TaskActivity {
  id: number;
  task: number;
  comment: string;
  filename: string | null;
  date_created: string;
  created_by: {
    id: number;
    user: {
      first_name: string;
      last_name: string;
    };
  } | null;
}

/**
 * Hook to fetch activity logs for a specific task
 * Returns activities sorted by date (newest first)
 */
export function useTaskActivity(taskId: number | null) {
  return useQuery({
    queryKey: ['activities', taskId],
    queryFn: async () => {
      if (!taskId) {
        return [];
      }

      const response = await apiRequest(
        `${API_ENDPOINTS.ACTIVITIES.LIST}?task=${taskId}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to fetch task activity'
        }));
        throw new Error(errorData.error || 'Failed to fetch task activity');
      }

      return response.json() as Promise<TaskActivity[]>;
    },
    enabled: !!taskId, // Only run query if taskId is provided
    staleTime: 60000, // 1 minute - activity logs don't change often except when added
  });
}

/**
 * Hook to add a manual comment to a task's activity log
 * Invalidates the activity query to show the new comment immediately
 */
export function useAddTaskComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, comment }: { taskId: number; comment: string }) => {
      const response = await apiRequest(API_ENDPOINTS.ACTIVITIES.LIST, {
        method: 'POST',
        body: JSON.stringify({
          task: taskId,
          comment: comment.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to add comment'
        }));
        throw new Error(errorData.error || 'Failed to add comment');
      }

      return response.json();
    },
    onSuccess: (data, variables) => {
      // Invalidate the activities query for this task to refetch and show the new comment
      queryClient.invalidateQueries({ queryKey: ['activities', variables.taskId] });
    },
  });
}
